import * as Api from '@ViewModels';
import * as React from 'react';
import { useAutoSave } from './useAutoSave';
import { useStepSaveGuard } from './useStepSaveGuard';
import { useAutomationFlowContext } from '../contexts/AutomationFlowContext';

/**
 * Base parameters for step state hooks
 */
interface UseBaseStepStateParams<TStep extends Api.IAutomationStep> {
	step: TStep | null;
	template: Api.IAutomationTemplate | null | undefined;
	stepId: string;
	readonly: boolean;
	onSaveSuccess?: () => void;
	onSaveError?: (error: Api.IOperationResultNoValue) => void;
	// Initial state configuration
	initialContactFilter?: Api.IContactsFilterRequest | null;
	hasAdvancedOptions?: (step: TStep) => boolean;
}

/**
 * Return type for base step state hook
 */
interface UseBaseStepStateReturn<TStep extends Api.IAutomationStep> {
	// Contact filter state
	skipStepContactFilter: Api.IContactsFilterRequest | null | undefined;
	setSkipStepContactFilter: (filter: Api.IContactsFilterRequest | null | undefined) => void;
	// UI state
	showRequiredFieldError: boolean;
	setShowRequiredFieldError: (show: boolean) => void;
	advancedOptionsToggled: boolean;
	setAdvancedOptionsToggled: (toggled: boolean) => void;
	// Auto save
	autoSave: ReturnType<typeof useAutoSave>;
	// Common operations
	clearInlineErrorMessage: () => void;
	// Base save function to be extended by specific implementations
	baseSave: (stepType: Api.AutomationStepType, buildStepModel: (step: TStep) => TStep) => Promise<void>;
}

/**
 * Base hook for managing common automation step state.
 * Encapsulates contact filters, validation, advanced options, and auto-save logic.
 *
 * This hook provides shared functionality that all step types need:
 * - Contact filter management
 * - Required field validation
 * - Advanced options toggle state
 * - Auto-save integration
 *
 * Specific step implementations should use this as a foundation and add their
 * step-specific logic on top.
 */
export function useBaseStepState<TStep extends Api.IAutomationStep>({
	step,
	template,
	stepId,
	readonly,
	onSaveSuccess,
	onSaveError,
	initialContactFilter,
	hasAdvancedOptions,
}: UseBaseStepStateParams<TStep>): UseBaseStepStateReturn<TStep> {
	// Impersonation context for admin operations (e.g., editing customer automations)
	const { impersonationContext } = useAutomationFlowContext();

	// Contact filter state
	const [skipStepContactFilter, setSkipStepContactFilter] = React.useState<
		Api.IContactsFilterRequest | null | undefined
	>(initialContactFilter ?? step?.contactFilter);

	// UI state
	const [showRequiredFieldError, setShowRequiredFieldError] = React.useState(false);
	const [advancedOptionsToggled, setAdvancedOptionsToggled] = React.useState(() => {
		if (hasAdvancedOptions && step) {
			return hasAdvancedOptions(step);
		}
		// Default check for common advanced options
		return !!(step?.contactFilter?.criteria || step?.sendFromOptions);
	});

	// Auto-save hook
	const autoSave = useAutoSave({
		templateId: template?.id,
		enabled: !readonly,
		onSaveSuccess,
		onSaveError,
		impersonationContext,
	});

	// Clear validation errors
	const clearInlineErrorMessage = React.useCallback(() => {
		setShowRequiredFieldError(false);
	}, []);

	const shouldSaveStep = useStepSaveGuard(step, autoSave.saveStatus);

	// Base save function that handles common logic
	const baseSave = React.useCallback(
		async (stepType: Api.AutomationStepType, buildStepModel: (step: TStep) => TStep) => {
			if (!step || !stepId || !template?.id) {
				// Return early - missing required data for save operation
				return Promise.resolve();
			}

			const stepModel = buildStepModel(step);

			// Skip phantom updates: content editors save on blur, so a focus/blur with no
			// actual edit would otherwise queue a no-op PUT.
			if (!shouldSaveStep(stepModel)) {
				return Promise.resolve();
			}

			return autoSave.saveStep(stepId, stepType, stepModel);
		},
		[step, stepId, template, autoSave, shouldSaveStep]
	);

	// Update contact filter when step changes
	// Use a ref to track if we need to sync, avoiding object comparison issues
	const stepContactFilterRef = React.useRef(step?.contactFilter);
	React.useEffect(() => {
		// Only update if the step's contactFilter reference has actually changed
		if (step?.contactFilter !== stepContactFilterRef.current) {
			stepContactFilterRef.current = step?.contactFilter;
			setSkipStepContactFilter(step?.contactFilter);
		}
	}, [step?.contactFilter]);

	return {
		skipStepContactFilter,
		setSkipStepContactFilter,
		showRequiredFieldError,
		setShowRequiredFieldError,
		advancedOptionsToggled,
		setAdvancedOptionsToggled,
		autoSave,
		clearInlineErrorMessage,
		baseSave,
	};
}
